import { Injectable, inject, signal } from '@angular/core';
import { ulid } from 'ulid';
import { UlidService } from './ulid.service';

/**
 * Page-level gate for WebSocket card data: drops payloads from superseded
 * `getPageData` batches (user date/filter refresh). Complements per-card ordering
 * in `DataService`.
 *
 * Floor ids are client ULIDs (same sortable shape as backend WebSocket `requestId`).
 *
 * **Do not** call `setRefreshFloor` before the post-`getCard` hydrate `getPageData` —
 * that rejects valid `getCard` WebSocket updates and leaves charts stuck loading.
 *
 * Full sequence diagrams and call-site table:
 * Storybook → Analytics / Cards / Data Flow → “Page runtime hydration”.
 */
@Injectable({ providedIn: 'root' })
export class PageDataRefreshCoordinatorService {
	private readonly ulidService = inject(UlidService);

	private readonly _floorRequestIdByPageId = signal<Map<string, string>>(
		new Map()
	);

	private _latestRefreshFloor: string | undefined;

	/**
	 * Raise the page WebSocket floor to this `getPageData` batch's `requestId`.
	 *
	 * Call **synchronously** when that ULID is created — before awaiting
	 * `buildRequestContext()`. `resolveRequester()` can yield; setting the floor
	 * only after that await lets stale table/chart cache land during a filter
	 * refresh.
	 *
	 * Floor must be the same `requestId` sent in the GraphQL variables.
	 * User-initiated `getPageData` only (not a post-`getCard` hydrate).
	 */
	setRefreshFloor(pageId: string, requestId: string): string {
		const key = this.#normalizePageId(pageId);
		const current = this._floorRequestIdByPageId().get(key);
		if (current && this.ulidService.compare(requestId, current) < 0) {
			return current;
		}
		const next = new Map(this._floorRequestIdByPageId());
		next.set(key, requestId);
		this._floorRequestIdByPageId.set(next);
		if (
			!this._latestRefreshFloor ||
			this.ulidService.compare(requestId, this._latestRefreshFloor) >= 0
		) {
			this._latestRefreshFloor = requestId;
		}
		return requestId;
	}

	/**
	 * @deprecated Prefer `setRefreshFloor` with the `getPageData` requestId.
	 * Kept for tests that only need a lexicographic floor without a live batch.
	 */
	beginRefresh(pageId: string): string {
		return this.setRefreshFloor(pageId, ulid());
	}

	/** Floor for a page batch, or the most recent refresh when pageId is unknown. */
	resolveRefreshFloor(pageId: string | undefined): string | undefined {
		if (pageId?.trim()) {
			const floor = this._floorRequestIdByPageId().get(
				this.#normalizePageId(pageId)
			);
			if (floor) {
				return floor;
			}
		}
		return this._latestRefreshFloor;
	}

	/** Latest client floor for a page refresh batch (`beginRefresh`), if any. */
	getFloorForPage(pageId: string | undefined): string | undefined {
		if (!pageId?.trim()) {
			return undefined;
		}
		return this._floorRequestIdByPageId().get(
			this.#normalizePageId(pageId)
		);
	}

	/**
	 * Filter option lists and analysis periods arrive on `getCard` / `getCardData`
	 * requestIds, not `getPageData` batches. Applying the page floor drops those
	 * in-flight WebSocket pushes when a later page hydrate raises the floor.
	 */
	static skipsPageDataRefreshFloor(cardType: string | undefined): boolean {
		return cardType === 'filter' || cardType === 'analysis';
	}

	/**
	 * Whether a WebSocket card payload should be applied for the given page.
	 * When `requestId` is missing, accepts (cannot order — matches DataService).
	 */
	shouldAcceptWebSocketUpdate(
		pageId: string | undefined,
		requestId: string | undefined
	): boolean {
		if (!requestId || !pageId) {
			return true;
		}

		const floor = this._floorRequestIdByPageId().get(
			this.#normalizePageId(pageId)
		);
		if (!floor) {
			return true;
		}

		return this.ulidService.compare(requestId, floor) >= 0;
	}

	clearPage(pageId: string): void {
		const key = this.#normalizePageId(pageId);
		const current = this._floorRequestIdByPageId();
		if (!current.has(key)) {
			return;
		}
		const next = new Map(current);
		next.delete(key);
		this._floorRequestIdByPageId.set(next);
	}

	/**
	 * Drop all page floors (report reload / version restore). Call before
	 * `getCard` so leftover `getPageData` ULIDs cannot reject runtime WS data.
	 */
	clearAll(): void {
		if (this._floorRequestIdByPageId().size === 0) {
			this._latestRefreshFloor = undefined;
			return;
		}
		this._floorRequestIdByPageId.set(new Map());
		this._latestRefreshFloor = undefined;
	}

	#normalizePageId(pageId: string): string {
		return pageId.startsWith('page_') ? pageId.slice(5) : pageId;
	}
}
